import { useTranslation } from "react-i18next";
import { Plus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { WizardFieldDef } from "@/services/platformEnterprisesService";

const FIELD_TYPES: WizardFieldDef["type"][] = [
  "text",
  "email",
  "number",
  "date",
  "textarea",
  "select",
  "file",
  "checkbox",
];

interface Props {
  fields: WizardFieldDef[];
  onChange: (fields: WizardFieldDef[]) => void;
}

function emptyField(): WizardFieldDef {
  return { key: "", type: "text", label: "", required: false };
}

export function WizardFieldListEditor({ fields, onChange }: Props): JSX.Element {
  const { t } = useTranslation();

  function updateField(idx: number, patch: Partial<WizardFieldDef>): void {
    onChange(fields.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
  }

  function removeField(idx: number): void {
    onChange(fields.filter((_, i) => i !== idx));
  }

  function addField(): void {
    onChange([...fields, emptyField()]);
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-xs uppercase tracking-wide text-muted-foreground">
          {t("enterpriseProductBuilder.fieldsLabel")}
        </Label>
        <Button size="sm" variant="outline" onClick={addField}>
          <Plus className="mr-1 h-3.5 w-3.5" />
          {t("enterpriseProductBuilder.addField")}
        </Button>
      </div>

      {fields.length === 0 && (
        <p className="rounded-md border border-dashed py-3 text-center text-xs text-muted-foreground">
          {t("enterpriseProductBuilder.noFieldsYet")}
        </p>
      )}

      {fields.map((f, idx) => (
        <div key={idx} className="space-y-2 rounded-md border bg-muted/30 p-3">
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">{t("enterpriseProductBuilder.fieldKey")}</Label>
              <Input
                value={f.key}
                onChange={(e) => updateField(idx, { key: e.target.value })}
                placeholder="snake_case_key"
                className="h-8 text-xs"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t("enterpriseProductBuilder.fieldType")}</Label>
              <Select
                value={f.type}
                onValueChange={(v) =>
                  updateField(idx, { type: v as WizardFieldDef["type"] })
                }
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FIELD_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {t(`enterpriseProductBuilder.fieldTypeLabel.${type}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">{t("enterpriseProductBuilder.fieldLabel")}</Label>
            <Input
              value={f.label}
              onChange={(e) => updateField(idx, { label: e.target.value })}
              className="h-8 text-xs"
            />
          </div>

          {f.type === "select" && (
            <div className="space-y-1">
              <Label className="text-xs">
                {t("enterpriseProductBuilder.fieldOptions")}
              </Label>
              <textarea
                value={(f.options ?? [])
                  .map((o) => `${o.value}|${o.label}`)
                  .join("\n")}
                onChange={(e) =>
                  updateField(idx, {
                    options: e.target.value
                      .split("\n")
                      .map((line) => line.trim())
                      .filter(Boolean)
                      .map((line) => {
                        const [value, label] = line.split("|");
                        return { value: value ?? "", label: label ?? value ?? "" };
                      }),
                  })
                }
                rows={3}
                placeholder="value|label"
                className="w-full rounded-md border border-input bg-background px-2 py-1.5 font-mono text-xs"
              />
            </div>
          )}

          <div className="flex items-center justify-between gap-2 pt-1">
            <label className="flex items-center gap-2 text-xs">
              <Checkbox
                checked={f.required ?? false}
                onCheckedChange={(c) => updateField(idx, { required: c === true })}
              />
              {t("enterpriseProductBuilder.fieldRequired")}
            </label>
            <Button
              size="sm"
              variant="ghost-destructive"
              className="h-7"
              onClick={() => removeField(idx)}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}
